import _ from 'lodash';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { type BaseToolPersistedState, BaseToolStore } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import type {
  ParameterDefinitions,
  ToolConfigParams,
  ToolParameter,
  ToolRehydratePayload,
} from '@/toolSelection/baseTool.store';
import type { OPERATOR } from '@/toolSelection/investigate.constants';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';
import type { Signal } from '@/utilities/items.types';

const VERSION_TWO = 'V2';
const VERSION_THREE = 'V3';

export type ValueSearchSelectableValue = string | number | { id: string } | null;

interface ValueSearchStoreState extends BaseToolPersistedState {
  limitsParams?: {
    entryCondition?: {
      durationUnits: string;
      operator: string;
      value: string;
      durationValue: number;
      duration: string | Record<string, unknown>;
      value2?: string;
    };
    exitCondition?: {
      durationUnits: string;
      operator: string;
      value: string;
      durationValue: number;
      duration: string | Record<string, unknown>;
    };
  };
  type?: string;
  version?: string;
  isSimple?: boolean;
  isCleansing?: boolean;
  isMigratedDeviationSearch?: boolean;
  useValidValues?: boolean;
  simpleOperator?: string;
  simpleValue?: string | number | boolean | null;
  simpleLowerValue?: string | number | boolean | null;
  simpleUpperValueInclusivity?: string;
  simpleLowerValueInclusivity?: string;
  minDuration?: string | Record<string, unknown>;
  mergeDuration?: string | Record<string, unknown>;
  advancedEntryOperator?: string;
  advancedEntryValue?: string | number | boolean | null;
  advancedEntryLowerValue?: string | number | boolean | null;
  advancedEntryDuration?: string | Record<string, unknown>;
  advancedExitOperator?: string;
  advancedExitValue?: string | number | boolean | null;
  advancedExitDuration?: string | Record<string, unknown>;
  inputSignal?: Signal;
}

type Config = ToolConfigParams & Partial<ValueSearchStoreState>;

export type ValueSearchDehydratedState = ValueSearchStoreState;

export const DEFAULT_DURATION: ValueWithUnitsItem = { value: 0, units: 'min' };

export class ValueSearchStore extends BaseToolStore {
  static readonly storeName = 'sqValueSearchStore';
  parameterDefinitions: ParameterDefinitions = {
    inputSignal: { predicate: ['name', 'a'] },
  };
  limitsParams: Record<string, unknown> = {};
  type = TREND_TOOLS.VALUE_SEARCH;

  initialize(): void {
    const initialState: ValueSearchStoreState = {
      ...BASE_TOOL_COMMON_PROPS,
      isSimple: true,
      useValidValues: false,
      isCleansing: false,
      simpleOperator: undefined,
      simpleValue: null,
      simpleUpperValueInclusivity: undefined,
      simpleLowerValue: null,
      simpleLowerValueInclusivity: undefined,
      minDuration: DEFAULT_DURATION,
      mergeDuration: DEFAULT_DURATION,
      advancedEntryOperator: undefined,
      advancedEntryValue: null,
      advancedEntryLowerValue: null,
      advancedEntryDuration: DEFAULT_DURATION,
      advancedExitOperator: undefined,
      advancedExitValue: null,
      advancedExitDuration: DEFAULT_DURATION,
      isMigratedDeviationSearch: false,
      version: VERSION_THREE,
    };
    this.state = this.immutable(initialState);
  }

  get isSimple(): boolean {
    return this.state.get('isSimple');
  }

  get inputSignal(): Signal {
    return this.state.get('inputSignal');
  }

  get isCleansing(): boolean {
    return this.state.get('isCleansing');
  }

  get useValidValues(): boolean {
    return this.state.get('useValidValues');
  }

  get simpleOperator(): OPERATOR {
    return this.state.get('simpleOperator');
  }

  get simpleValue(): string | number | null {
    return this.state.get('simpleValue');
  }

  get simpleUpperValueInclusivity(): OPERATOR {
    return this.state.get('simpleUpperValueInclusivity');
  }

  get simpleLowerValue(): string | number | null {
    return this.state.get('simpleLowerValue');
  }

  get simpleLowerValueInclusivity(): OPERATOR {
    return this.state.get('simpleLowerValueInclusivity');
  }

  get minDuration(): ValueWithUnitsItem {
    return this.state.get('minDuration');
  }

  get mergeDuration(): ValueWithUnitsItem {
    return this.state.get('mergeDuration');
  }

  get advancedEntryOperator(): OPERATOR {
    return this.state.get('advancedEntryOperator');
  }

  get advancedEntryValue(): string | number | null {
    return this.state.get('advancedEntryValue');
  }

  get advancedEntryLowerValue(): string | number | null {
    return this.state.get('advancedEntryLowerValue');
  }

  get advancedEntryDuration(): ValueWithUnitsItem {
    return this.state.get('advancedEntryDuration');
  }

  get advancedExitOperator(): OPERATOR {
    return this.state.get('advancedExitOperator');
  }

  get advancedExitValue(): string | number | null {
    return this.state.get('advancedExitValue');
  }

  get advancedExitDuration(): ValueWithUnitsItem {
    return this.state.get('advancedExitDuration');
  }

  get isMigratedDeviationSearch(): boolean {
    return this.state.get('isMigratedDeviationSearch');
  }

  /**
   * Exports the state that is not ephemeral (such as progress indicators).
   *
   * @returns {Object} The dehydrated parameters.
   */
  dehydrate(): ValueSearchDehydratedState {
    return this.state.serialize();
  }

  /**
   * Re-creates the value search store.
   *
   * @param {Object} dehydratedState Previous state usually obtained from `dehydrate` method.
   */
  rehydrate(dehydratedState: Partial<ValueSearchDehydratedState>): void {
    this.state.merge(dehydratedState);
  }

  /**
   * Update UI config property when its loaded
   * @param  {Object} oldConfig - containing the conditions to remap
   * @return {Object} result of the remapping (if it was necessary)
   */
  migrateSavedConfig<TConfig extends ToolConfigParams>(
    config: TConfig,
    _parameters: ToolParameter[],
    _formula?: string,
  ): TConfig {
    const workingConfig = this.migrateSavedConfig1(config as TConfig);

    switch (workingConfig.version) {
      case undefined: {
        const v2Config = this.updateToSaveConfigV2(workingConfig);
        this.updateToSaveConfigV3(v2Config);
        break;
      }
      case VERSION_TWO:
        this.updateToSaveConfigV3(workingConfig);
        break;
      default:
        break;
    }

    return config;
  }

  /**
   * Remaps the entry and exit conditions from the store in the format { durationValue, durationUnits, duration }
   * to { duration: { value, units } } to maintain compatibility with older versions. This function is called by the
   * baseToolStore as it rehydrates the payload
   * @param  {Object} oldConfig - containing the conditions to remap
   * @return {Object} result of the remapping (if it was necessary)
   */
  migrateSavedConfig1<TConfig extends Config>(oldConfig: TConfig): TConfig {
    // Keeping this old config just to be safe, but bypassing it if we have a V3 config already
    if (oldConfig.version === VERSION_THREE) {
      return oldConfig;
    }

    _.forEach([oldConfig?.limitsParams?.entryCondition, oldConfig?.limitsParams?.exitCondition], (condition) => {
      if (!condition) return;
      const { durationValue, durationUnits } = condition;
      if (!_.isUndefined(durationValue) && !_.isUndefined(durationUnits)) {
        condition.duration = {
          units: durationUnits,
          value: durationValue,
        };
        Reflect.deleteProperty(condition, 'durationUnits');
        Reflect.deleteProperty(condition, 'durationValue');
      }
    });

    return oldConfig;
  }

  /**
   * Remaps config version to version 2
   *
   * @param  {Object} oldConfig - containing the conditions to remap
   * @return {Object} result of the remapping (if it was necessary)
   */

  updateToSaveConfigV2<TConfig extends Config>(oldConfig: TConfig): TConfig {
    oldConfig.version = VERSION_TWO;

    if (oldConfig?.limitsParams?.entryCondition?.operator === '=') {
      oldConfig.limitsParams.entryCondition.operator = '~=';
    } else if (oldConfig?.limitsParams?.entryCondition?.operator === '!=') {
      oldConfig.limitsParams.entryCondition.operator = '!~';
    }

    if (oldConfig?.limitsParams?.exitCondition?.operator === '=') {
      oldConfig.limitsParams.exitCondition.operator = '~=';
    } else if (oldConfig?.limitsParams?.exitCondition?.operator === '!=') {
      oldConfig.limitsParams.exitCondition.operator = '!~';
    }

    // If we have an existing value search, we want it to display in advanced mode
    if (_.isUndefined(oldConfig.isSimple)) {
      oldConfig.isSimple = false;
    }

    return oldConfig;
  }

  updateToSaveConfigV3<TConfig extends Config>(v2Config: TConfig): TConfig {
    v2Config.version = VERSION_THREE;

    if (v2Config.isSimple) {
      v2Config.simpleOperator = v2Config?.limitsParams?.entryCondition?.operator;
      v2Config.simpleValue = v2Config?.limitsParams?.entryCondition?.value;
      v2Config.simpleLowerValue = v2Config?.limitsParams?.entryCondition?.value2;
      v2Config.minDuration = v2Config?.limitsParams?.entryCondition?.duration;
      v2Config.mergeDuration = v2Config?.limitsParams?.exitCondition?.duration;
    } else {
      v2Config.advancedEntryOperator = v2Config?.limitsParams?.entryCondition?.operator;
      v2Config.advancedEntryValue = v2Config?.limitsParams?.entryCondition?.value;
      v2Config.advancedEntryLowerValue = v2Config?.limitsParams?.entryCondition?.value2;
      v2Config.advancedEntryDuration = v2Config?.limitsParams?.entryCondition?.duration;
      v2Config.advancedExitOperator = v2Config?.limitsParams?.exitCondition?.operator;
      v2Config.advancedExitValue = v2Config?.limitsParams?.exitCondition?.value;
      v2Config.advancedExitDuration = v2Config?.limitsParams?.exitCondition?.duration;
    }

    delete v2Config.limitsParams;
    return v2Config;
  }

  protected readonly handlers: BaseToolStore['handlers'] & {
    VALUE_SEARCH_SET_SIMPLE_OPERATOR: (payload: { simpleOperator: OPERATOR }) => void;
    VALUE_SEARCH_SET_SIMPLE_VALUE: (payload: { simpleValue: string | number }) => void;
    VALUE_SEARCH_SET_SIMPLE_UPPER_VALUE_INCLUSIVITY: (payload: { simpleUpperValueInclusivity: OPERATOR }) => void;
    VALUE_SEARCH_SET_SIMPLE_LOWER_VALUE: (payload: { simpleLowerValue: string | number }) => void;
    VALUE_SEARCH_SET_SIMPLE_LOWER_VALUE_INCLUSIVITY: (payload: { simpleLowerValueInclusivity: OPERATOR }) => void;
    VALUE_SEARCH_SET_ADVANCED_ENTRY_OPERATOR: (payload: { advancedEntryOperator: OPERATOR }) => void;
    VALUE_SEARCH_SET_ADVANCED_ENTRY_VALUE: (payload: { advancedEntryValue: string | number }) => void;
    VALUE_SEARCH_SET_ADVANCED_ENTRY_LOWER_VALUE: (payload: { advancedEntryLowerValue: string | number }) => void;
    VALUE_SEARCH_SET_ADVANCED_ENTRY_DURATION: (payload: { advancedEntryDuration: ValueWithUnitsItem }) => void;
    VALUE_SEARCH_SET_ADVANCED_EXIT_OPERATOR: (payload: { advancedExitOperator: OPERATOR }) => void;
    VALUE_SEARCH_SET_ADVANCED_EXIT_VALUE: (payload: { advancedExitValue: string | number }) => void;
    VALUE_SEARCH_SET_ADVANCED_EXIT_DURATION: (payload: { advancedExitDuration: ValueWithUnitsItem }) => void;
    VALUE_SEARCH_SET_MIN_DURATION: (payload: { minDuration: ValueWithUnitsItem }) => void;
    VALUE_SEARCH_SET_MERGE_DURATION: (payload: { mergeDuration: ValueWithUnitsItem }) => void;
    VALUE_SEARCH_SET_IS_MIGRATED_DEVIATION_SEARCH: (payload: { isMigratedDeviationSearch: boolean }) => void;
    VALUE_SEARCH_SET_SEARCH_TYPE: (payload: { isSimple: boolean }) => void;
    VALUE_SEARCH_TOGGLE_CLEANSING: () => void;
    VALUE_SEARCH_TOGGLE_VALID_VALUES: () => void;
    TOOL_REHYDRATE_FOR_EDIT: (payload: ToolRehydratePayload) => void;
  } = {
    ...this.baseHandlers,

    VALUE_SEARCH_SET_SIMPLE_OPERATOR: (payload: { simpleOperator: OPERATOR }) => {
      this.state.set('simpleOperator', payload.simpleOperator);
    },

    VALUE_SEARCH_SET_SIMPLE_VALUE: (payload: { simpleValue: string | number }) => {
      this.state.set('simpleValue', payload.simpleValue);
    },

    VALUE_SEARCH_SET_SIMPLE_UPPER_VALUE_INCLUSIVITY: (payload: { simpleUpperValueInclusivity: OPERATOR }) => {
      this.state.set('simpleUpperValueInclusivity', payload.simpleUpperValueInclusivity);
    },

    VALUE_SEARCH_SET_SIMPLE_LOWER_VALUE: (payload: { simpleLowerValue: string | number }) => {
      this.state.set('simpleLowerValue', payload.simpleLowerValue);
    },

    VALUE_SEARCH_SET_SIMPLE_LOWER_VALUE_INCLUSIVITY: (payload: { simpleLowerValueInclusivity: OPERATOR }) => {
      this.state.set('simpleLowerValueInclusivity', payload.simpleLowerValueInclusivity);
    },

    VALUE_SEARCH_SET_ADVANCED_ENTRY_OPERATOR: (payload: { advancedEntryOperator: OPERATOR }) => {
      this.state.set('advancedEntryOperator', payload.advancedEntryOperator);
    },

    VALUE_SEARCH_SET_ADVANCED_ENTRY_VALUE: (payload: { advancedEntryValue: string | number }) => {
      this.state.set('advancedEntryValue', payload.advancedEntryValue);
    },

    VALUE_SEARCH_SET_ADVANCED_ENTRY_LOWER_VALUE: (payload: { advancedEntryLowerValue: string | number }) => {
      this.state.set('advancedEntryLowerValue', payload.advancedEntryLowerValue);
    },

    VALUE_SEARCH_SET_ADVANCED_ENTRY_DURATION: (payload: { advancedEntryDuration: ValueWithUnitsItem }) => {
      this.state.set('advancedEntryDuration', payload.advancedEntryDuration);
    },

    VALUE_SEARCH_SET_ADVANCED_EXIT_OPERATOR: (payload: { advancedExitOperator: OPERATOR }) => {
      this.state.set('advancedExitOperator', payload.advancedExitOperator);
    },

    VALUE_SEARCH_SET_ADVANCED_EXIT_VALUE: (payload: { advancedExitValue: string | number }) => {
      this.state.set('advancedExitValue', payload.advancedExitValue);
    },

    VALUE_SEARCH_SET_ADVANCED_EXIT_DURATION: (payload: { advancedExitDuration: ValueWithUnitsItem }) => {
      this.state.set('advancedExitDuration', payload.advancedExitDuration);
    },

    VALUE_SEARCH_SET_MIN_DURATION: (payload: { minDuration: ValueWithUnitsItem }) => {
      this.state.set('minDuration', payload.minDuration);
    },

    VALUE_SEARCH_SET_MERGE_DURATION: (payload: { mergeDuration: ValueWithUnitsItem }) => {
      this.state.set('mergeDuration', payload.mergeDuration);
    },

    VALUE_SEARCH_SET_IS_MIGRATED_DEVIATION_SEARCH: (payload: { isMigratedDeviationSearch: boolean }) => {
      this.state.set('isMigratedDeviationSearch', payload.isMigratedDeviationSearch);
    },

    VALUE_SEARCH_SET_SEARCH_TYPE: (payload: { isSimple: boolean }) => {
      this.state.set('isSimple', payload.isSimple);
    },

    VALUE_SEARCH_TOGGLE_CLEANSING: () => {
      this.state.set('isCleansing', !this.state.get('isCleansing'));
    },

    VALUE_SEARCH_TOGGLE_VALID_VALUES: () => {
      this.state.set('useValidValues', !this.state.get('useValidValues'));
    },

    TOOL_REHYDRATE_FOR_EDIT: (payload: ToolRehydratePayload) => {
      if (payload.type !== TREND_TOOLS.VALUE_SEARCH) {
        return;
      }

      this.rehydrateForEdit(payload);

      const simpleValueParam = _.find(payload.parameters ?? [], { name: 'b' }) as ToolParameter | undefined;
      if (simpleValueParam?.item?.id) {
        this.state.set('simpleValue', simpleValueParam.item.id);
      }

      const simpleLowerValueParam = _.find(payload.parameters ?? [], { name: 'c' }) as ToolParameter | undefined;
      if (simpleLowerValueParam?.item?.id) {
        this.state.set('simpleLowerValue', simpleLowerValueParam.item.id);
      }
    },
  };
}
